import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { InfoHint, PageTitle, Panel, SegmentedControl, SourceChip } from '../../components/c';
import { EmptyYearState } from '../../components/ui/EmptyYearState';
import { ErrorState } from '../../components/ui/ErrorState';
import { DashboardSkeleton } from '../../components/ui/Skeleton';
import { buildApiUrl, fetchDashboardData, fetchMyWeek, fetchWeeklyBrief, type MyWeekPayload } from '../../lib/api';
import { formatCurrency, toNumber } from '../../lib/format';
import type { DashboardPayload } from '../../lib/types';
import { useUiStore } from '../../store/ui';
import { SOURCE_PROJECTION_QUERY_OPTIONS } from '../shared/integrationProjection';
import { SourceFixDetail } from '../shared/FixAction';
import { useBusinessOsNavigation } from '../shared/navigation';
import {
  DASHBOARD_VIEWS,
  buildSourceChips,
  dashboardPeriod,
  invoicesToSendInPeriod,
  defaultWeekStart,
  financialMetricsOf,
  isoDate,
  mondayOf,
  monthKeyOf,
  monthTitle,
  optionalNumber,
  parseIsoDate,
  parseView,
  resolveMonthKey,
  resolveWeekStart,
  shiftMonthKey,
  shiftWeekStart,
  shortDate,
  weekRangeLabel,
  withIdentity,
  yearOfIso,
  type DashboardView,
  type NavTarget,
  type SourceInput,
} from './dashboardModel';
import { InvoicesToSendPanel, MonthView, WeekView, YearView } from './DashboardViews';
import './dashboard-c.css';

function normalizeAiMessage(message: unknown): string {
  const raw = String(message || '').trim();
  if (!raw) return 'Erreur lors de la génération du brief.';
  if (raw.includes('OPENAI_API_KEY') || raw.includes('IA externe desactive')) {
    return "Assistant IA désactivé. Le brief hebdomadaire standard reste généré par règles métier locales ; aucune donnée n'est envoyée à OpenAI.";
  }
  return raw;
}

function hasNonZeroValue(map: unknown): boolean {
  if (!map || typeof map !== 'object') return false;
  return Object.values(map as Record<string, unknown>).some((value) => toNumber(value) !== 0);
}

/**
 * A dashboard is "empty" for an exercise when none of its money, production,
 * pipeline, invoicing, capacity or priority signals carry any value. Robust by
 * construction: any single non-zero signal keeps the real widgets on screen, so
 * this only ever fires on a genuinely dataless year.
 */
export function isDashboardEmpty(data: DashboardPayload | undefined): boolean {
  if (!data) return false;
  const record = data as unknown as Record<string, unknown>;
  const financial = (record.financial_metrics || {}) as Record<string, unknown>;
  const trust = (record.trust_summary || {}) as Record<string, unknown>;
  const quality = (record.data_quality_summary || {}) as Record<string, unknown>;
  const financialSignals = [
    'produced_revenue',
    'signed_revenue',
    'year_revenue_forecast',
    'billed_revenue_ht',
    'paid_revenue_ht',
    'cash_collected',
    'pipeline_gross',
    'pipeline_weighted',
  ];
  const hasSignal =
    financialSignals.some((key) => toNumber(financial[key]) !== 0)
    || hasNonZeroValue(data.monthly_realise)
    || hasNonZeroValue(data.pipeline_status_ca)
    || hasNonZeroValue(data.pipeline_status_counts)
    || hasNonZeroValue(data.facture_status_montant)
    || hasNonZeroValue(data.facture_status_counts)
    || (Array.isArray(data.monthly_load_summary) && data.monthly_load_summary.length > 0)
    || (Array.isArray(data.action_groups) && data.action_groups.length > 0)
    || (Array.isArray(data.trust_watchlist) && data.trust_watchlist.length > 0)
    || toNumber(trust.issue_count) !== 0
    || toNumber(quality.issue_count) !== 0
    || toNumber(record.ca_produit_ytd) !== 0
    || toNumber(record.jours_produits_ytd) !== 0
    || toNumber(record.jours_vendus) !== 0;
  return !hasSignal;
}

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d={direction === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
    </svg>
  );
}

function PeriodNav({
  previousLabel,
  nextLabel,
  onPrevious,
  onNext,
  reset,
  children,
}: {
  previousLabel: string;
  nextLabel: string;
  onPrevious: () => void;
  onNext: () => void;
  reset?: { label: string; onClick: () => void } | null;
  children: ReactNode;
}) {
  return (
    <div className="dash-c-period">
      <button type="button" className="c-icon-button" aria-label={previousLabel} onClick={onPrevious}>
        <Chevron direction="left" />
      </button>
      <span className="dash-c-period__label" aria-live="polite">{children}</span>
      <button type="button" className="c-icon-button" aria-label={nextLabel} onClick={onNext}>
        <Chevron direction="right" />
      </button>
      {reset ? (
        <button type="button" className="dash-c-link" onClick={reset.onClick}>{reset.label}</button>
      ) : null}
    </div>
  );
}

function YearSubtitle({ data, year, today }: { data: DashboardPayload | undefined; year: number; today: Date }) {
  const metrics = financialMetricsOf(data);
  const forecast = optionalNumber(metrics.year_revenue_forecast);
  const coverage = optionalNumber(metrics.year_revenue_split_coverage_pct);
  const unventilated = toNumber(metrics.year_revenue_unventilated_count);
  const asOf = shortDate(isoDate(today), year);
  return (
    <div className="dash-c-period">
      <span className="dash-c-period__label num">
        Exercice {year}
        {data ? (forecast !== null ? <> · <strong>{formatCurrency(forecast)}</strong> prévus au {asOf}</> : ' · prévu non calculé, voir « Pipeline ouvert » et « Acquis »') : null}
      </span>
      <InfoHint label={`Comment se calcule le prévu ${year}`}>
        <p className="dash-c-hint">
          Prévu {year} = acquis + pipeline pondéré, part {year} (HubSpot). Une affaire signée en {year} mais
          livrée en {year + 1} n’y figure pas ; une affaire signée avant {year} y figure pour sa part {year}.
        </p>
        {coverage !== null ? <p className="dash-c-hint num">Ventilation annuelle renseignée à {coverage.toLocaleString('fr-FR')} %.</p> : null}
        {unventilated > 0 ? (
          <p className="dash-c-hint num">
            {unventilated} affaire(s) sans ventilation annuelle dans HubSpot sont rattachées à l’année de leur date de clôture.
          </p>
        ) : null}
      </InfoHint>
    </div>
  );
}

function sourceInputs(view: DashboardView, dashboard: DashboardPayload | undefined, myWeek: MyWeekPayload | undefined, year: number): SourceInput[] {
  if (view === 'annee') {
    if (!dashboard) return [];
    const metrics = financialMetricsOf(dashboard);
    const sources = (metrics.sources || {}) as Record<string, unknown>;
    const coverage = (metrics.coverage || {}) as Record<string, unknown>;
    return [
      { label: 'HubSpot', meta: sources.pipeline, outsideYearCount: optionalNumber(coverage.hubspot_open_deals_outside_year_count), connector: 'hubspot', year },
      { label: 'Qonto', meta: sources.invoices, connector: 'qonto', year },
      { label: 'Qonto', meta: sources.cash, connector: 'qonto', year },
      { label: 'Fibery', meta: null, internal: true },
    ];
  }
  if (!myWeek?.sources) return [];
  const sources = myWeek.sources;
  const inputs: SourceInput[] = [{ label: 'HubSpot', meta: sources.pipeline, connector: 'hubspot', year }];
  if (view === 'semaine') inputs.push({ label: 'Qonto', meta: sources.facturation, connector: 'qonto', year });
  inputs.push({ label: 'Fibery', meta: null, internal: true }, { label: 'Digiforma', meta: sources.sessions_training, connector: 'digiforma', year });
  return inputs;
}

export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedYear = useUiStore((state) => state.selectedYear);
  const setSelectedYear = useUiStore((state) => state.setSelectedYear);
  const openTarget = useBusinessOsNavigation();

  const todayIso = isoDate(new Date());
  const today = useMemo(() => parseIsoDate(todayIso) as Date, [todayIso]);
  const view = parseView(searchParams.get('vue'));
  const weekStart = resolveWeekStart(searchParams.get('semaine'), selectedYear, today);
  const monthKey = resolveMonthKey(searchParams.get('mois'), selectedYear, today);
  // The month view reads the week payload only for the undated deals, which do
  // not depend on the week: it shares the cached current week.
  const myWeekStart = view === 'semaine' ? weekStart : defaultWeekStart(selectedYear, today);

  const dashboardQuery = useQuery({
    queryKey: ['dashboard', selectedYear],
    queryFn: () => fetchDashboardData(selectedYear),
    ...SOURCE_PROJECTION_QUERY_OPTIONS,
  });
  const myWeekQuery = useQuery({
    queryKey: ['my-week', selectedYear, myWeekStart],
    queryFn: () => fetchMyWeek(selectedYear, myWeekStart),
    enabled: view !== 'annee',
    ...SOURCE_PROJECTION_QUERY_OPTIONS,
  });

  const [briefLoading, setBriefLoading] = useState(false);
  const [briefText, setBriefText] = useState<string | null>(null);

  const data = dashboardQuery.data;
  const dashboardEmpty = useMemo(() => isDashboardEmpty(data), [data]);

  const onOpen = useCallback((target: NavTarget | null) => {
    if (target) openTarget(withIdentity(target));
  }, [openTarget]);

  const updateParams = useCallback((patch: Record<string, string | null>) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(patch)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const goToWeek = (next: string) => {
    const year = yearOfIso(next);
    if (year !== selectedYear) setSelectedYear(year);
    updateParams({ semaine: next });
  };
  const goToMonth = (next: string) => {
    const year = yearOfIso(next);
    if (year !== selectedYear) setSelectedYear(year);
    updateParams({ mois: next });
  };
  const currentWeek = isoDate(mondayOf(today));
  const currentMonth = monthKeyOf(today);

  const periodSubtitle = view === 'semaine' ? (
    <PeriodNav
      previousLabel="Semaine précédente"
      nextLabel="Semaine suivante"
      onPrevious={() => goToWeek(shiftWeekStart(weekStart, -1))}
      onNext={() => goToWeek(shiftWeekStart(weekStart, 1))}
      reset={weekStart !== currentWeek ? { label: 'Revenir à cette semaine', onClick: () => goToWeek(currentWeek) } : null}
    >
      Semaine du <strong>{weekRangeLabel(weekStart)}</strong>
    </PeriodNav>
  ) : view === 'mois' ? (
    <PeriodNav
      previousLabel="Mois précédent"
      nextLabel="Mois suivant"
      onPrevious={() => goToMonth(shiftMonthKey(monthKey, -1))}
      onNext={() => goToMonth(shiftMonthKey(monthKey, 1))}
      reset={monthKey !== currentMonth ? { label: 'Revenir à ce mois', onClick: () => goToMonth(currentMonth) } : null}
    >
      <strong>{monthTitle(monthKey)}</strong>
    </PeriodNav>
  ) : (
    <YearSubtitle data={data} year={selectedYear} today={today} />
  );

  const chips = buildSourceChips(sourceInputs(view, data, myWeekQuery.data, selectedYear));

  const runBrief = async () => {
    setBriefLoading(true);
    try {
      const result = await fetchWeeklyBrief(selectedYear);
      setBriefText(normalizeAiMessage(result.brief));
    } catch (error) {
      setBriefText(normalizeAiMessage(error instanceof Error ? error.message : 'Erreur lors de la génération du brief.'));
    } finally {
      setBriefLoading(false);
    }
  };

  return (
    <div className="page-stack dash-c">
      <PageTitle
        title="Tableau de bord"
        controls={(
          <SegmentedControl
            label="Période du tableau de bord"
            options={DASHBOARD_VIEWS}
            value={view}
            onChange={(next) => updateParams({ vue: next })}
          />
        )}
        subtitle={periodSubtitle}
        actions={(
          <>
            {chips.map((chip) => (
              <SourceChip
                key={chip.key}
                state={chip.state}
                detail={chip.state !== 'ok' && (chip.details.length || chip.fix) ? (
                  <>
                    {chip.details.length ? <ul className="dash-c-source-detail">{chip.details.map((line) => <li key={line}>{line}</li>)}</ul> : null}
                    <SourceFixDetail fix={chip.fix} />
                  </>
                ) : undefined}
              >
                {chip.text}
              </SourceChip>
            ))}
            <button className="button button--primary dash-c-brief" type="button" disabled={briefLoading} onClick={runBrief}>
              {briefLoading ? 'Génération…' : 'Brief hebdo'}
            </button>
            <a className="button button--ghost" href={buildApiUrl(`/api/export-workbook?year=${selectedYear}`)} download>
              Exporter tout
            </a>
          </>
        )}
      />

      {briefText ? (
        <Panel
          title="Brief hebdomadaire"
          aside={<button className="button button--compact" type="button" onClick={() => setBriefText(null)}>Fermer</button>}
        >
          <div className="dash-c-brief-text">{briefText}</div>
        </Panel>
      ) : null}

      {dashboardQuery.isLoading ? <DashboardSkeleton /> : null}
      {dashboardQuery.isError ? (
        <ErrorState message={(dashboardQuery.error as Error).message} onRetry={() => dashboardQuery.refetch()} />
      ) : null}

      {data && dashboardEmpty ? (
        <EmptyYearState
          year={selectedYear}
          onReturnToCurrentYear={() => setSelectedYear(new Date().getFullYear())}
          description="Aucune production, facturation ni pipeline enregistré pour cet exercice."
        />
      ) : null}

      {data && !dashboardEmpty ? (
        <div className={`dash-c-view dash-c-view--${view}`} role="tabpanel" aria-label={DASHBOARD_VIEWS.find((option) => option.value === view)?.label}>
          {view === 'semaine' ? (
            <WeekView dashboard={data} myWeek={myWeekQuery} year={selectedYear} weekStart={weekStart} today={today} onOpen={onOpen} />
          ) : null}
          {view === 'mois' ? (
            <MonthView dashboard={data} myWeek={myWeekQuery} year={selectedYear} monthKey={monthKey} today={today} onOpen={onOpen} />
          ) : null}
          {view === 'annee' ? <YearView dashboard={data} year={selectedYear} today={today} onOpen={onOpen} /> : null}
          <InvoicesToSendPanel
            invoices={invoicesToSendInPeriod(data, dashboardPeriod(view, selectedYear, weekStart, monthKey), today)}
            periodLabel={view === 'semaine' ? 'cette semaine' : view === 'mois' ? 'ce mois-ci' : `en ${selectedYear}`}
            onOpen={onOpen}
          />
        </div>
      ) : null}
    </div>
  );
}
